import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { supabase } from 'lib/supabase';
import PasswordResetForm from './components/PasswordResetForm';
import InvalidLinkState from './components/InvalidLinkState';

const ResetPassword = () => {
  const [isValidSession, setIsValidSession] = useState(null); // null = checking
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Supabase sets the session from the URL hash automatically
    // We check if a recovery session is present
    const checkSession = async () => {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (session?.user) {
        setIsValidSession(true);
      } else {
        // Listen for the PASSWORD_RECOVERY event from the URL hash
        const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
          if (event === 'PASSWORD_RECOVERY' && session) {
            setIsValidSession(true);
          } else if (event === 'SIGNED_IN' && session) {
            setIsValidSession(true);
          }
        });

        // Give it a moment to process the hash
        setTimeout(() => {
          setIsValidSession(prev => prev === null ? false : prev);
        }, 2000);

        return () => subscription?.unsubscribe();
      }
    };

    checkSession();
  }, []);

  const handlePasswordReset = async ({ password }) => {
    setIsLoading(true);
    setError('');

    try {
      const { error: updateError } = await supabase?.auth?.updateUser({
        password
      });

      if (updateError) {
        throw updateError;
      }

      setIsSuccess(true);

      // Redirect to success page after brief delay
      setTimeout(() => {
        navigate('/password-reset-success');
      }, 1500);
    } catch (err) {
      if (err?.message?.includes('same password')) {
        setError('New password must be different from your current password.');
      } else if (err?.message?.includes('weak')) {
        setError('Password is too weak. Please choose a stronger password.');
      } else {
        setError(err?.message || 'Failed to update password. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
            <Icon name="Scale" size={32} color="white" />
          </div>
          <h1 className="text-2xl font-heading font-bold text-text-primary mb-2">LawDesk KE</h1>
          <p className="text-text-secondary">Legal Practice Management System</p>
        </div>

        {/* Card */}
        <div className="bg-surface rounded-lg shadow-card border border-border p-8">
          {isValidSession === null ? (
            // Loading state while checking session
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent mx-auto mb-4"></div>
              <p className="text-text-secondary text-sm">Verifying your reset link...</p>
            </div>
          ) : isValidSession ? (
            <PasswordResetForm
              onSubmit={handlePasswordReset}
              isLoading={isLoading}
              isSuccess={isSuccess}
              error={error}
            />
          ) : (
            <InvalidLinkState />
          )}
        </div>

        {!isSuccess && isValidSession !== null && (
          <div className="text-center mt-6">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-primary transition-smooth"
            >
              <Icon name="ArrowLeft" size={16} />
              Back to Sign In
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;